import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, laneLabel } from "@/lib/format";
import {
  CASE_STATUSES, SERVICE_LANES, URGENCY_LEVELS, HOW_FOUND_OPTIONS,
  TONE, labelOf, optOf,
} from "@/config/caseOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { FolderHeart, Plus, Search, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const CaseStepBadge = ({ value }) => {
  const o = optOf(CASE_STATUSES, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid="case-step-badge">
      {o?.label || value || "—"}
    </span>
  );
};

const UrgencyPill = ({ value }) => {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  const o = optOf(URGENCY_LEVELS, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: t.color }}>
      {(o?.tone === "red") && <AlertTriangle size={12} />} {o?.label || value}
    </span>
  );
};

const emptyNew = {
  case_name: "", family_name: "", loved_one_name: "", how_found: "", service_lane: "",
  urgency_level: "", assigned_user_id: "", branch_id: "", summary: "", main_issue: "",
};

export default function Cases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("cases");

  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", case_status: "all", service_lane: "all", urgency_level: "all", assigned_user_id: "all", branch_id: "all" });

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(emptyNew);
  const [creating, setCreating] = useState(false);

  const staffName = (id) => staff.find((s) => s.id === id)?.full_name || "Unassigned";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      ["case_status", "service_lane", "urgency_level", "assigned_user_id", "branch_id"].forEach((k) => {
        if (filters[k] && filters[k] !== "all") params[k] = filters[k];
      });
      const { data } = await api.get("/cases", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data.items || [])).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => {
    setNewForm({ ...emptyNew, assigned_user_id: user?.id || "", branch_id: branches.length === 1 ? branches[0].id : "" });
    setNewOpen(true);
  };

  const createCase = async (e) => {
    e.preventDefault();
    if (!newForm.case_name && !newForm.family_name) { toast.error("A Family Case name is required"); return; }
    setCreating(true);
    try {
      const payload = {
        case_name: newForm.case_name || newForm.family_name,
        family_name: newForm.family_name || newForm.case_name,
        loved_one_name: newForm.loved_one_name || undefined,
        how_found: newForm.how_found || undefined,
        service_lane: newForm.service_lane || undefined,
        urgency_level: newForm.urgency_level || undefined,
        assigned_user_id: newForm.assigned_user_id || undefined,
        summary: newForm.summary || undefined,
        main_issue: newForm.main_issue || undefined,
        case_status: "new",
      };
      if (newForm.branch_id) payload.branch_id = newForm.branch_id;
      const { data } = await api.post("/cases", payload);
      toast.success("Family Case created");
      setNewOpen(false);
      navigate(`/cases/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create Family Case");
    } finally { setCreating(false); }
  };

  const FilterSelect = ({ value, onChange, options, testid, all }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[150px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">{all}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2">
            <FolderHeart size={22} className="text-[#1C4358]" /> Family Cases
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Every family you are helping through their senior transition — organized by Current Step.</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-case-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New Family Case
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search family, loved one, case #..." className="pl-9 h-9" data-testid="case-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex gap-2">
            <FilterSelect value={filters.case_status} onChange={(v) => setFilters((p) => ({ ...p, case_status: v }))} options={CASE_STATUSES} testid="filter-step" all="All steps" />
            <FilterSelect value={filters.service_lane} onChange={(v) => setFilters((p) => ({ ...p, service_lane: v }))} options={SERVICE_LANES} testid="filter-lane" all="All lanes" />
            <FilterSelect value={filters.urgency_level} onChange={(v) => setFilters((p) => ({ ...p, urgency_level: v }))} options={URGENCY_LEVELS} testid="filter-urgency" all="Any urgency" />
            <FilterSelect value={filters.assigned_user_id} onChange={(v) => setFilters((p) => ({ ...p, assigned_user_id: v }))} options={staff.map((s) => ({ value: s.id, label: s.full_name }))} testid="filter-assigned" all="Anyone" />
            <FilterSelect value={filters.branch_id} onChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))} options={branches.map((b) => ({ value: b.id, label: b.name }))} testid="filter-branch" all="All branches" />
          </div>
        </div>
      </Card>

      {/* List */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
              <FolderHeart size={26} className="text-[#1C4358]" />
            </div>
            <p className="font-medium text-[#252221]">No family cases yet</p>
            <p className="text-sm text-muted-foreground">Convert a consultation, or start a new Family Case to begin helping a family.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="cases-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Family Case</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Current Step</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Service lane</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Urgency</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Assigned</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} className="hover:bg-[rgba(155,185,206,0.10)] cursor-pointer" data-testid="case-row" onClick={() => navigate(`/cases/${c.id}`)}>
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{c.case_name || c.family_name || "Family Case"}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.case_number}{c.loved_one_name ? ` · For ${c.loved_one_name}` : ""}
                      </div>
                    </TableCell>
                    <TableCell><CaseStepBadge value={c.case_status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.service_lane ? laneLabel(c.service_lane) : "—"}</TableCell>
                    <TableCell><UrgencyPill value={c.urgency_level} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.assigned_user_name || staffName(c.assigned_user_id)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/cases/${c.id}`); }} data-testid="open-case-button">
                        Open <ChevronRight size={15} className="ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{items.length} family case{items.length === 1 ? "" : "s"}</p>

      {/* New Family Case dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto mmm-scroll" data-testid="new-case-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><FolderHeart size={20} className="text-[#1C4358]" /> New Family Case</DialogTitle>
            <DialogDescription>Open a case to coordinate this family's senior transition.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createCase} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Family Case name *</Label><Input value={newForm.case_name} onChange={(e) => setNewForm((p) => ({ ...p, case_name: e.target.value, family_name: e.target.value }))} placeholder="e.g. The Johnson Family" data-testid="case-name-input" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Loved one's name</Label><Input value={newForm.loved_one_name} onChange={(e) => setNewForm((p) => ({ ...p, loved_one_name: e.target.value }))} data-testid="case-loved-one-input" /></div>
              <div><Label className="mb-1.5 block text-[13px]">How they found us</Label>
                <Select value={newForm.how_found} onValueChange={(v) => setNewForm((p) => ({ ...p, how_found: v }))}>
                  <SelectTrigger data-testid="case-how-found"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{HOW_FOUND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Primary service lane</Label>
                <Select value={newForm.service_lane} onValueChange={(v) => setNewForm((p) => ({ ...p, service_lane: v }))}>
                  <SelectTrigger data-testid="case-lane-new"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Urgency</Label>
                <Select value={newForm.urgency_level} onValueChange={(v) => setNewForm((p) => ({ ...p, urgency_level: v }))}>
                  <SelectTrigger data-testid="case-urgency-new"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{URGENCY_LEVELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Assigned team member</Label>
                <Select value={newForm.assigned_user_id} onValueChange={(v) => setNewForm((p) => ({ ...p, assigned_user_id: v }))}>
                  <SelectTrigger data-testid="case-assigned-new"><SelectValue placeholder="Assign" /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {branches.length > 1 && (
                <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Branch</Label>
                  <Select value={newForm.branch_id} onValueChange={(v) => setNewForm((p) => ({ ...p, branch_id: v }))}>
                    <SelectTrigger data-testid="case-branch-new"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Main issue</Label><Input value={newForm.main_issue} onChange={(e) => setNewForm((p) => ({ ...p, main_issue: e.target.value }))} placeholder="The family's primary concern" data-testid="case-main-issue-new" /></div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Case summary</Label><Textarea rows={2} value={newForm.summary} onChange={(e) => setNewForm((p) => ({ ...p, summary: e.target.value }))} data-testid="case-summary-new" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="case-create-button">{creating ? "Creating..." : "Create Family Case"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
